/**
 * FLY3D-1085 — the deploy-payload contract.
 *
 * `tailscale-deploy.yml` moved to a SINGLE `payload` input: a JSON
 * object whose keys the workflow reads with
 * `fromJSON(inputs.payload).<key>`. GitHub caps `workflow_dispatch` at
 * 10 inputs, so collapsing every parameter into one object is what lets
 * the contract grow — a new parameter is a schema property, never a new
 * workflow input, and never new dashboard input plumbing.
 *
 * Everything in this module is driven by
 * `src/config/deploy-payload.schema.json`, a verbatim copy of
 * IDI-PROD-DEPLOY's `docs/deployment/deploy-payload.schema.json`
 * (@ 953a65b). Validation and the operator form both read that file, so
 * the two cannot drift from each other — and re-syncing with the deploy
 * repo is a one-file copy.
 */
import schema from '@/config/deploy-payload.schema.json'

/**
 * Minimal draft-07 node, covering the keywords the deploy schema uses.
 * Anything richer belongs in IDI-PROD-DEPLOY's own CI, not in the
 * dashboard's pre-dispatch guard.
 */
type SchemaNode = {
  type?: string
  enum?: readonly unknown[]
  const?: unknown
  minLength?: number
  pattern?: string
  anyOf?: readonly SchemaNode[]
  default?: unknown
  description?: string
}

const properties = schema.properties as Record<string, SchemaNode>
const requiredKeys = schema.required as readonly string[]

/** Every key the contract accepts, read off the schema. */
export type DeployPayloadKey = keyof typeof schema.properties

/**
 * The object the dashboard stringifies into `inputs.payload`.
 *
 * `correlation_id` is not here: `deployDock()` stamps it on at dispatch
 * so a caller can never dispatch with a trace id that doesn't match the
 * WebSocket subscription it opened (FLY3D-612).
 */
export type DeployPayload = {
  machine: string
  dock_type: string
  run_docker_compose?: boolean
  dji_gateway_sn?: string
  mqtt_host?: string
  dock_id?: string
  rc_id?: string
  organisation_id?: string
  container_versions?: string
}

/**
 * Compile-time drift guard: if the schema gains a property that
 * `DeployPayload` doesn't declare, this errors. Widening the type is the
 * only dashboard change a new key needs — the form and the validator
 * pick it up from the schema on their own.
 */
type _SchemaKeysAreTyped =
  Exclude<DeployPayloadKey, 'correlation_id' | keyof DeployPayload> extends never
    ? true
    : ['deploy-payload.schema.json has keys missing from DeployPayload']
const _schemaKeysAreTyped: _SchemaKeysAreTyped = true
void _schemaKeysAreTyped

/**
 * Keys the dashboard derives from the dock record / deploy code rather
 * than asking the operator for — the dock's identity, its broker and
 * whether the stack comes up, all of which the dashboard owns as source
 * of truth. Everything else in the schema is rendered as a form field,
 * so a future key is operator-editable with no plumbing.
 */
export const DERIVED_PAYLOAD_KEYS: readonly string[] = [
  'machine',
  'dock_type',
  'dock_id',
  'rc_id',
  'organisation_id',
  'dji_gateway_sn',
  'mqtt_host',
  'run_docker_compose',
  'correlation_id',
]

/** A form control derived from one schema property. */
export type DeployFormField = {
  key: string
  /** `enum` renders a select, `boolean` a checkbox, everything else text. */
  control: 'text' | 'checkbox' | 'select'
  label: string
  description: string
  options: readonly string[]
  default: string | boolean
}

/** Turns `container_versions` into `Container versions`. */
const humanise = (key: string): string => {
  const words = key.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The operator-editable half of the schema, as form fields. Driven by
 * the schema so the form and the validation can never disagree about
 * what a deploy accepts.
 */
export const deployFormFields = (): DeployFormField[] =>
  Object.entries(properties)
    .filter(([key]) => !DERIVED_PAYLOAD_KEYS.includes(key))
    .map(([key, node]) => {
      const options = (node.enum ?? []).filter((v): v is string => typeof v === 'string')
      const control: DeployFormField['control'] =
        options.length > 0 ? 'select' : node.type === 'boolean' ? 'checkbox' : 'text'
      return {
        key,
        control,
        label: humanise(key),
        description: node.description ?? '',
        options,
        default:
          (node.default as string | boolean | undefined) ?? (control === 'checkbox' ? false : ''),
      }
    })

/**
 * Starting state for the deploy form: every operator-editable key at its
 * schema default. Seeded from the schema so a new property arrives in
 * the form already defaulted.
 */
export const deployFormDefaults = (): Record<string, string | boolean> =>
  Object.fromEntries(deployFormFields().map((field) => [field.key, field.default]))

/**
 * Folds the form's state back into payload keys, dropping blank text
 * fields so an untouched option falls back to the dock's env-file
 * channel rather than being dispatched as an empty override.
 */
export const deployFormToPayload = (
  model: Record<string, string | boolean>,
): Partial<DeployPayload> => {
  const out: Record<string, string | boolean> = {}
  for (const field of deployFormFields()) {
    const value = model[field.key]
    if (value === undefined) continue
    if (field.control === 'checkbox') {
      out[field.key] = Boolean(value)
      continue
    }
    const text = String(value).trim()
    if (text) out[field.key] = text
  }
  return out as Partial<DeployPayload>
}

/** Validates one value against one schema node. Returns human-readable errors. */
const checkNode = (value: unknown, node: SchemaNode, key: string): string[] => {
  const errors: string[] = []

  if (node.const !== undefined && value !== node.const) {
    errors.push(`${key} must be ${JSON.stringify(node.const)}`)
  }
  if (node.type === 'string' && typeof value !== 'string') {
    errors.push(`${key} must be a string`)
    return errors
  }
  if (node.type === 'boolean' && typeof value !== 'boolean') {
    errors.push(`${key} must be true or false`)
    return errors
  }
  if (node.type === 'number' || node.type === 'integer') {
    if (typeof value !== 'number' || Number.isNaN(value)) {
      errors.push(`${key} must be a number`)
      return errors
    }
  }
  if (node.enum && !node.enum.includes(value)) {
    errors.push(`${key} must be one of ${node.enum.join(', ')} (got ${JSON.stringify(value)})`)
  }
  if (typeof value === 'string') {
    if (node.minLength !== undefined && value.length < node.minLength) {
      errors.push(`${key} is required`)
    }
    if (node.pattern !== undefined && !new RegExp(node.pattern).test(value)) {
      errors.push(`${key} is malformed (got ${JSON.stringify(value)})`)
    }
  }
  // `anyOf` passes when ANY branch validates; only then do its branch
  // errors get dropped. mqtt_host uses it for "empty or an IPv4".
  if (node.anyOf && !node.anyOf.some((branch) => checkNode(value, branch, key).length === 0)) {
    errors.push(`${key} is not in an accepted form (got ${JSON.stringify(value)})`)
  }

  return errors
}

/**
 * Validates a candidate payload against the schema. Returns an empty
 * array when valid; otherwise one message per problem, ready to show
 * the operator.
 */
export const validateDeployPayload = (payload: unknown): string[] => {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
    return ['Deploy payload must be an object']
  }
  const candidate = payload as Record<string, unknown>
  const errors: string[] = []

  for (const key of requiredKeys) {
    if (candidate[key] === undefined || candidate[key] === null) {
      errors.push(`${key} is required`)
    }
  }
  for (const [key, value] of Object.entries(candidate)) {
    if (value === undefined) continue
    const node = properties[key]
    // additionalProperties: false — a stray key (a `release` left over
    // from the old contract, say) is rejected here rather than by a 422
    // from GitHub.
    if (!node) {
      errors.push(`${key} is not a deploy parameter`)
      continue
    }
    errors.push(...checkNode(value, node, key))
  }

  return errors
}

/**
 * Validates a single key against the schema, for inline field-level
 * feedback in the deploy form. Same code path as the pre-dispatch
 * check, so the form can never accept something the dispatch rejects.
 */
export const validateDeployField = (key: string, value: unknown): string[] => {
  const node = properties[key]
  if (!node) return [`${key} is not a deploy parameter`]
  if (value === undefined || value === null) {
    return requiredKeys.includes(key) ? [`${key} is required`] : []
  }
  return checkNode(value, node, key)
}

/** A dock record the mqtt_host guard can check a candidate host against. */
export type MqttHostCheckTarget = {
  deviceID?: string
  mqtt_host?: string | null
}

/** Strict dotted-quad: four 0-255 octets, no leading zeros. */
const isIpv4 = (host: string): boolean =>
  /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/.test(host) &&
  host.split('.').every((octet) => {
    if (octet.length > 1 && octet.startsWith('0')) return false
    const n = Number(octet)
    return n >= 0 && n <= 255
  })

/**
 * The safety guard that moved out of `environments/<machine>.json` and
 * into the dashboard (FLY3D-1085).
 *
 * Server-side, `mqtt_host` is only format-checked, so a well-formed but
 * WRONG IP sails through and crash-loops the dock — that is what
 * happened on 2026-09-24. The dashboard is the source of truth for the
 * dock's broker, so it is the only place that can catch a wrong-but-
 * well-formed address: it cross-checks the value being dispatched
 * against the host on the dock's own record.
 *
 * @returns an error message to show the operator, or null when the host
 *          is safe to dispatch.
 */
export const validateMqttHostForDock = (
  host: string | undefined | null,
  dock: MqttHostCheckTarget = {},
): string | null => {
  const candidate = (host ?? '').trim()
  const dockName = dock.deviceID ? `dock ${dock.deviceID}` : 'this dock'

  if (!candidate) {
    return `No MQTT broker host for ${dockName}. Set the dock's broker IP (admin → Device Fields) before deploying.`
  }
  if (!isIpv4(candidate)) {
    return `MQTT broker host "${candidate}" is not an IPv4 address. The dock needs a dotted-quad address such as 192.168.2.107.`
  }
  // Addresses that parse but can never be a broker the dock can reach.
  const [firstOctet] = candidate.split('.')
  if (candidate === '0.0.0.0' || candidate === '255.255.255.255' || firstOctet === '127') {
    return `MQTT broker host "${candidate}" is not reachable from ${dockName}. Use the broker's address on the dock's network.`
  }
  // The wrong-but-well-formed case the server cannot catch.
  const known = (dock.mqtt_host ?? '').trim()
  if (known && known !== candidate) {
    return `MQTT broker host "${candidate}" does not match the host recorded for ${dockName} ("${known}"). Deploying a wrong broker IP crash-loops the dock — fix the deploy code or the device record before retrying.`
  }

  return null
}

export { schema as deployPayloadSchema }
